<template>
  <div
    class="message-with-reactions"
    @mouseenter="showDesktopControls"
    @mouseleave="hideDesktopControls"
  >
    <Transition v-if="!isMobile && !isMessageAutomatic" name="pop">
      <div
        v-if="isPanelVisible"
        class="desktop-reactions-panel"
        :class="{ isMine }"
      >
        <DesktopReactionsControlsItem
          :active-icon="IconType.EMOJI_BLACK"
          :inactive-icon="IconType.EMOJI_GRAY"
          :current-icon="currentIcon"
          @on-active-icon-change="safeTransformReactionsPanel"
          @toggle-active-icon="toggleActiveIcon"
        >
          <MessageEmojiReactionsPanel
            v-if="currentIcon === IconType.EMOJI_BLACK"
            :style="styleObj"
            :message-id="message.id"
            :is-mine="isMine"
            @close-panel="hideDesktopControls"
          />
        </DesktopReactionsControlsItem>
        <DesktopReactionsControlsItem
          :active-icon="IconType.REPLY"
          :inactive-icon="IconType.REPLY_GRAY"
          :current-icon="currentIcon"
          @toggle-active-icon="toggleActiveIcon"
          @click="replyToMessage"
        />
        <DesktopReactionsControlsItem
          :active-icon="IconType.DOTS_BLACK"
          :inactive-icon="IconType.DOTS"
          :current-icon="currentIcon"
          @toggle-active-icon="toggleActiveIcon"
          @click="showActions"
        >
          <DesktopOtherActions
            v-if="isActionsOpen"
            :is-mine="isMine"
            :message="message"
            @close-actions="closeActions"
          />
        </DesktopReactionsControlsItem>
      </div>
    </Transition>
    <slot />
  </div>
  <div
    v-if="
      !getIsAutomaticMessageType(message) &&
      message.reactions?.visibleItems?.length
    "
    class="current-emoji-reactions"
    :class="{ isMine }"
    @click="openReactionsModal"
  >
    <EmojiItem
      v-for="(emoji, ind) in message.reactions.visibleItems"
      :key="ind"
      :size="isMobile ? 12 : 20"
      :emoji="emoji"
    />
    <span v-if="message.reactions.isCountVisible" class="reactions-count">{{
      message.reactions.count
    }}</span>
  </div>
</template>

<script setup lang="ts">
import EmojiItem from "@/components/cards/EmojiItem/EmojiItem.vue";
import { getIsAutomaticMessageType } from "@/utils/message";
import { Message } from "@/store/chats/types";
import { computed, HTMLAttributes, ref } from "vue";
import { IconType } from "@/types/icons";
import MessageEmojiReactionsPanel from "../MessageEmojiReactionsPanel/MessageEmojiReactionsPanel.vue";
import DesktopReactionsControlsItem from "../DesktopReactionsControlsItem/DesktopReactionsControlsItem.vue";
import { GetterTypes } from "@/store";
import { useComputedValue, useGetMessageHandlers } from "@/composables";
import { LayoutTypes } from "@/store/app/state";
import { config } from "@/config";
import DesktopOtherActions from "../DesktopOtherActions/DesktopOtherActions.vue";

interface ChatMessageReactionsProps {
  message: Message;
  isMine: boolean;
  elementId: string;
}

interface Emits {
  (e: "openModal"): void;
}

const emit = defineEmits<Emits>();

const { handleReplyToMessage } = useGetMessageHandlers();

const props = defineProps<ChatMessageReactionsProps>();
const isDesktopControlsVisible = ref(false);
const layoutType = useComputedValue<LayoutTypes>(GetterTypes.GET_LAYOUT_TYPE);
const isMobile = computed(() => layoutType.value === LayoutTypes.MOBILE);
const isMessageAutomatic = computed(() =>
  getIsAutomaticMessageType(props.message),
);
const isActionsOpen = ref(false);
const transformStyle = ref("");
const styleObj = computed<HTMLAttributes["style"]>(() => ({
  transform: transformStyle.value,
}));
const currentIcon = ref<IconType | null>(null);
const isReactingEnabled = computed(
  () => !getIsAutomaticMessageType(props.message) && !props.message.isDeleted,
);

const showActions = () => {
  isActionsOpen.value = true;
};
const closeActions = () => {
  isActionsOpen.value = false;
};

const isPanelVisible = computed(() => {
  const isVisible =
    isReactingEnabled.value &&
    (isDesktopControlsVisible.value || isActionsOpen.value);

  return isVisible;
});

const openReactionsModal = () => {
  emit("openModal");
};

const toggleDesktopControls = (newValue: boolean) => {
  if (isMobile.value || !isReactingEnabled.value) {
    return;
  }
  isDesktopControlsVisible.value = newValue;
};

const showDesktopControls = () => {
  toggleDesktopControls(true);
};
const hideDesktopControls = () => {
  toggleDesktopControls(false);
  currentIcon.value = null;
};

const getTransformStyle = (width: number) => {
  let addition: number = width + 16;
  if (props.isMine) {
    addition = -addition;
  }

  return `translateX(calc(-50% + ${addition}px))`;
};

const toggleActiveIcon = (icon: IconType) => {
  currentIcon.value = icon;
};

const safeTransformReactionsPanel = () => {
  const contentEl = document.getElementById(props.elementId);
  if (!contentEl) {
    return;
  }
  const { width } = contentEl.getBoundingClientRect();
  // if the width of reactions panel does not overflow the chat wrapper, do nothing
  if (width >= config.constants.REACTIONS_SAFE_WIDTH) {
    return;
  }
  transformStyle.value = getTransformStyle(width);
};

const replyToMessage = () => {
  handleReplyToMessage(props.message);
};
</script>

<style lang="scss">
@import "./MessageWithReactions.scss";
</style>
